import { zodResolver } from '@hookform/resolvers/zod';
import { CircleUser, List, Smartphone } from 'lucide-react';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { FormError } from '@/components/ui/form-error';
import { OtpCodeInput } from '@/components/ui/otp-code-input';
import { mfaCodeSchema } from '@/utils/schemas';

const mfaSignInSchema = z.object({
  mfaCode: mfaCodeSchema,
});

type MfaSignInForm = z.infer<typeof mfaSignInSchema>;

type CodeType = 'totp' | 'backup';

interface MfaSignInFormProps {
  // ログインしようとしているアカウント
  email: string;
  onSubmit: (mfaCode: string) => void;
  // メールアドレスとパスワードの入力に戻る
  onBack: () => void;
  isPending: boolean;
  // API が返したコードの検証エラー(コード誤り・ロック中)
  errorMessage?: string;
  // 認証アプリのコードとバックアップコードを切り替えた(前のコードのエラーを消すために使う)
  onCodeTypeChange: () => void;
}

/**
 * ログインの 2 段階目。認証アプリの6桁コードを入力する。
 * 認証アプリを使えない場合はバックアップコードの入力に切り替えられる
 */
export const MfaSignInForm = ({
  email,
  onSubmit,
  onBack,
  isPending,
  errorMessage,
  onCodeTypeChange,
}: MfaSignInFormProps) => {
  const [codeType, setCodeType] = useState<CodeType>('totp');
  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<MfaSignInForm>({
    resolver: zodResolver(mfaSignInSchema),
    defaultValues: { mfaCode: '' },
  });

  const submit = handleSubmit((data) => onSubmit(data.mfaCode));

  const switchCodeType = (next: CodeType) => {
    reset({ mfaCode: '' });
    setCodeType(next);
    onCodeTypeChange();
  };

  const codeError = errors.mfaCode?.message ?? errorMessage;

  return (
    <form onSubmit={submit} className="space-y-5">
      <p className="-mt-3 flex items-center gap-1.5 text-sm text-neutral-500">
        <CircleUser className="w-5 h-5" />
        {email}
      </p>

      {codeType === 'totp' ? (
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <Smartphone className="w-9 h-9 shrink-0 text-neutral-700" />
            <div>
              <label htmlFor="mfa-signin-code" className="font-bold">
                認証アプリの6桁のコード
              </label>
              <p className="text-sm text-neutral-500">
                スマートフォンの認証アプリで「アニマルオフィス」のコードを確認してください。
              </p>
            </div>
          </div>
          <div className="flex flex-col items-center">
            <Controller
              name="mfaCode"
              control={control}
              render={({ field }) => (
                <OtpCodeInput
                  {...field}
                  id="mfa-signin-code"
                  // 6桁そろったら送信する。送信中の二重送信は防ぐ
                  onComplete={() => {
                    if (!isPending) submit();
                  }}
                  disabled={isPending}
                  invalid={!!codeError}
                  autoFocus
                />
              )}
            />
            <FormError message={codeError} />
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <List className="w-9 h-9 shrink-0 text-neutral-700" />
            <div>
              <label htmlFor="mfa-signin-backup-code" className="font-bold">
                バックアップコード
              </label>
              <p className="text-sm text-neutral-500">
                保管しておいたバックアップコードのうち、まだ使っていないものを1つ入力してください。
              </p>
            </div>
          </div>
          <div>
            <input
              id="mfa-signin-backup-code"
              {...register('mfaCode')}
              type="text"
              autoComplete="off"
              placeholder="xxxxx-xxxxx"
              // biome-ignore lint/a11y/noAutofocus: 切り替えた直後にそのまま入力できるようにする
              autoFocus
              className="text-center font-mono text-xl tracking-wider"
            />
            <FormError message={codeError} />
            <p className="mt-1 text-sm text-neutral-500">
              使ったコードは再び使えません。ログイン後に再発行できます。
            </p>
          </div>
        </div>
      )}

      <Button type="submit" fullWidth disabled={isPending} loading={isPending}>
        ログインする
      </Button>

      <div className="flex flex-col items-center gap-3 border-t border-neutral-200 pt-4">
        <button
          type="button"
          onClick={() => switchCodeType(codeType === 'totp' ? 'backup' : 'totp')}
          disabled={isPending}
          className="text-sm text-sky-800 underline hover:text-sky-900 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {codeType === 'totp'
            ? 'スマートフォンを使えない場合(バックアップコード)'
            : '認証アプリのコードを使う'}
        </button>
        {/* 送信中に戻って別アカウントでログインすると、後から届いた応答で認証状態が上書きされるため無効にする */}
        <button
          type="button"
          onClick={onBack}
          disabled={isPending}
          className="text-sm text-neutral-600 underline hover:text-neutral-900 disabled:cursor-not-allowed disabled:opacity-50"
        >
          別のアカウントでログインする
        </button>
      </div>
    </form>
  );
};
